import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAuth from '../../auth/feature/auth';
import {
  adminService,
  IEditionSummary,
  ILicenseActivityEvent
} from '../service/admin.service';

// The edition slugs ("animworks", "licensespring") are API identifiers and are
// never translated; only their display labels are.
const EDITION_LABEL_KEYS: Record<string, string> = {
  animworks: 'admin.licenseActivity.editions.animworks',
  licensespring: 'admin.licenseActivity.editions.licensespring'
};

// Product display names are brand names — verbatim in every language.
const PRODUCT_LABELS: Record<string, string> = {
  'squarebit-subdivs': 'Squarebit Subdivs',
  touchpose: 'TouchPose'
};

const productLabel = (product: string) => PRODUCT_LABELS[product] ?? product;

const editionLabel = (edition: string, t: TFunction) =>
  EDITION_LABEL_KEYS[edition] ? t(EDITION_LABEL_KEYS[edition]) : edition;

const cardLabel = (
  summary: { product: string; edition: string },
  t: TFunction
) => `${productLabel(summary.product)} — ${editionLabel(summary.edition, t)}`;

// Event names are API identifiers — shown as returned, never translated.
const EVENT_OPTIONS = ['activate_attempt', 'activate', 'check', 'deactivate'];
// Result values are license status identifiers — shown as returned.
const RESULT_OPTIONS = [
  'Active',
  'Inactive',
  'Expired',
  'Revoked',
  'SeatLimit',
  'InvalidKey',
  'Indeterminate',
  'Blocked'
];

const resultBadgeClass = (result: string | null) => {
  switch (result) {
    case 'Active':
      return 'bg-badge-green-bg border-badge-green-text text-badge-green-text';
    case 'Blocked':
    case 'Revoked':
    case 'Expired':
      return 'bg-badge-red-bg border-badge-red-text text-badge-red-text';
    default:
      return 'bg-badge-sky-bg border-badge-sky-text text-badge-sky-text';
  }
};

const KillSwitchCard = ({
  summary,
  onToggle
}: {
  summary: IEditionSummary;
  onToggle: (summary: IEditionSummary, enable: boolean) => void;
}) => {
  const { t } = useTranslation();
  const label = cardLabel(summary, t);
  return (
    <div className="bg-surface-base rounded-xl shadow-lg p-[20px] flex-1 min-w-[280px]">
      <div className="flex items-center justify-between mb-[10px]">
        <h3 className="text-[15px] font-display font-bold text-content-dark">{label}</h3>
        <span
          className={`inline-block px-[9px] py-[3px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] uppercase ${
            summary.activationsEnabled
              ? 'bg-badge-green-bg border-badge-green-text text-badge-green-text'
              : 'bg-badge-red-bg border-badge-red-text text-badge-red-text'
          }`}
        >
          {summary.activationsEnabled
            ? t('admin.licenseActivity.card.activationsEnabled')
            : t('admin.licenseActivity.card.activationsPaused')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-[8px] text-[13px] text-gray-600 mb-[14px]">
        <div>
          <Trans
            i18nKey="admin.licenseActivity.card.activations"
            values={{ value: summary.activations }}
            components={{ usec: <span className="font-bold text-content-dark" /> }}
          />
        </div>
        <div>
          <Trans
            i18nKey="admin.licenseActivity.card.uniqueKeys"
            values={{ value: summary.uniqueKeys }}
            components={{ usec: <span className="font-bold text-content-dark" /> }}
          />
        </div>
        <div>
          <Trans
            i18nKey="admin.licenseActivity.card.uniqueMachines"
            values={{ value: summary.uniqueMachines }}
            components={{ usec: <span className="font-bold text-content-dark" /> }}
          />
        </div>
        <div>
          <Trans
            i18nKey="admin.licenseActivity.card.blockedAttempts"
            values={{ value: summary.blockedAttempts }}
            components={{ usec: <span className="font-bold text-content-dark" /> }}
          />
        </div>
      </div>
      <button
        onClick={() => onToggle(summary, !summary.activationsEnabled)}
        className={`w-full py-[8px] rounded-[8px] text-[13px] font-medium transition-colors ${
          summary.activationsEnabled
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        {summary.activationsEnabled
          ? t('admin.licenseActivity.card.pause')
          : t('admin.licenseActivity.card.resume')}
      </button>
      {summary.configUpdatedBy && (
        <p className="mt-[8px] text-[11px] text-gray-500">
          {summary.configUpdatedAt
            ? t('admin.licenseActivity.card.lastChangedOn', {
                user: summary.configUpdatedBy,
                date: new Date(summary.configUpdatedAt).toLocaleString()
              })
            : t('admin.licenseActivity.card.lastChangedBy', {
                user: summary.configUpdatedBy
              })}
        </p>
      )}
    </div>
  );
};

export const LicenseActivitySection = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [product, setProduct] = useState('');
  const [edition, setEdition] = useState('');
  const [eventType, setEventType] = useState('');
  const [result, setResult] = useState('');
  const [keySearch, setKeySearch] = useState('');
  const [confirmToggle, setConfirmToggle] = useState<{
    product: string;
    edition: string;
    enable: boolean;
  } | null>(null);

  const pageSize = 50;

  const summaryQuery = useQuery({
    queryKey: ['license-activity-summary'],
    queryFn: () => adminService.getLicenseActivitySummary(),
    enabled: isAdmin,
    refetchInterval: 5 * 60 * 1000
  });

  const listQuery = useQuery({
    queryKey: ['license-activity', page, product, edition, eventType, result, keySearch],
    queryFn: () =>
      adminService.getLicenseActivity({
        page,
        pageSize,
        product: product || undefined,
        edition: edition || undefined,
        event: eventType || undefined,
        result: result || undefined,
        key: keySearch || undefined
      }),
    enabled: isAdmin,
    refetchInterval: 60 * 1000
  });

  const toggleMutation = useMutation({
    mutationFn: ({
      product: prod,
      edition: ed,
      enable
    }: {
      product: string;
      edition: string;
      enable: boolean;
    }) => adminService.setEditionActivations(prod, ed, enable),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['license-activity-summary'] });
    }
  });

  if (!isAdmin) return null;

  const events = listQuery.data?.events ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const selectClass =
    'bg-surface-base border border-gray-300 rounded-[8px] px-[10px] py-[6px] text-[13px] text-content-dark';

  return (
    <div className="flex flex-col gap-[16px]">
      {/* Kill-switch / summary cards */}
      <div className="flex flex-wrap gap-[16px]">
        {(summaryQuery.data?.editions ?? []).map((summary) => (
          <KillSwitchCard
            key={`${summary.product}-${summary.edition}`}
            summary={summary}
            onToggle={(s, enable) =>
              setConfirmToggle({ product: s.product, edition: s.edition, enable })
            }
          />
        ))}
      </div>

      {/* Filters */}
      <div className="bg-surface-base rounded-xl shadow-lg p-[16px] flex flex-wrap gap-[10px] items-center">
        <select
          className={selectClass}
          value={product}
          onChange={(e) => {
            setProduct(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('admin.licenseActivity.filters.allProducts')}</option>
          <option value="squarebit-subdivs">
            {PRODUCT_LABELS['squarebit-subdivs']}
          </option>
          <option value="touchpose">{PRODUCT_LABELS.touchpose}</option>
        </select>
        <select
          className={selectClass}
          value={edition}
          onChange={(e) => {
            setEdition(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('admin.licenseActivity.filters.allEditions')}</option>
          {/* "Animworks" is a company name — verbatim. */}
          <option value="animworks">Animworks</option>
          <option value="licensespring">
            {t('admin.licenseActivity.editions.licensespring')}
          </option>
        </select>
        <select
          className={selectClass}
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('admin.licenseActivity.filters.allEvents')}</option>
          {EVENT_OPTIONS.map((ev) => (
            <option key={ev} value={ev}>
              {ev}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={result}
          onChange={(e) => {
            setResult(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('admin.licenseActivity.filters.allResults')}</option>
          {RESULT_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          className={`${selectClass} flex-1 min-w-[180px]`}
          placeholder={t('admin.licenseActivity.filters.keyPlaceholder')}
          value={keySearch}
          onChange={(e) => {
            setKeySearch(e.target.value);
            setPage(1);
          }}
        />
        <span className="text-[12px] text-gray-500 ml-auto">
          {t('admin.licenseActivity.eventTotal', { value: total })}
        </span>
      </div>

      {/* Events table */}
      <div className="bg-surface-base rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-gray-500 border-b border-gray-200">
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.time')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.product')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.edition')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.event')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.result')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.licenseKey')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.machine')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.validUntil')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.seats')}
                </th>
                <th className="px-[14px] py-[10px]">
                  {t('admin.licenseActivity.columns.plugin')}
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev: ILicenseActivityEvent) => (
                <tr
                  key={ev.id}
                  className="border-b border-gray-100 text-content-dark"
                >
                  <td className="px-[14px] py-[8px] whitespace-nowrap">
                    {new Date(ev.createdAt).toLocaleString()}
                  </td>
                  <td className="px-[14px] py-[8px] whitespace-nowrap">
                    {productLabel(ev.product)}
                  </td>
                  <td className="px-[14px] py-[8px]">
                    {ev.edition === 'animworks'
                      ? 'Animworks'
                      : t('admin.licenseActivity.editionRetail')}
                  </td>
                  <td className="px-[14px] py-[8px] font-mono text-[12px]">
                    {ev.event}
                  </td>
                  <td className="px-[14px] py-[8px]">
                    {ev.result ? (
                      <span
                        className={`inline-block px-[9px] py-[3px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] uppercase ${resultBadgeClass(
                          ev.result
                        )}`}
                      >
                        {ev.result}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-[14px] py-[8px] font-mono text-[12px]">
                    {ev.licenseKey}
                  </td>
                  <td
                    className="px-[14px] py-[8px] font-mono text-[11px] text-gray-500"
                    title={ev.hardwareId}
                  >
                    {ev.hardwareId.slice(0, 10)}…
                  </td>
                  <td className="px-[14px] py-[8px] whitespace-nowrap">
                    {ev.validUntil
                      ? new Date(ev.validUntil).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-[14px] py-[8px]">
                    {ev.seatsMax != null ? `${ev.seatsUsed ?? '?'}/${ev.seatsMax}` : '—'}
                  </td>
                  <td className="px-[14px] py-[8px] text-gray-500">
                    {ev.pluginVersion ?? '—'}
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-[14px] py-[24px] text-center text-gray-400"
                  >
                    {listQuery.isLoading
                      ? t('common.loading')
                      : t('admin.licenseActivity.noEvents')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-[14px] py-[10px] border-t border-gray-200 text-[13px]">
          <button
            className="px-[12px] py-[5px] rounded-[6px] bg-gray-100 hover:bg-gray-200 text-content-dark disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
          >
            {t('admin.licenseActivity.pagination.previous')}
          </button>
          <span className="text-gray-500">
            {t('admin.licenseActivity.pagination.page', {
              page,
              total: totalPages
            })}
          </span>
          <button
            className="px-[12px] py-[5px] rounded-[6px] bg-gray-100 hover:bg-gray-200 text-content-dark disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          >
            {t('admin.licenseActivity.pagination.next')}
          </button>
        </div>
      </div>

      {/* Kill-switch confirm modal */}
      {confirmToggle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-base rounded-[20px] shadow-xl px-[32px] py-[28px] w-full mx-[20px] max-w-[440px]">
            <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
              {confirmToggle.enable
                ? t('admin.licenseActivity.confirm.resumeTitle')
                : t('admin.licenseActivity.confirm.pauseTitle')}
            </h2>
            <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
              {confirmToggle.enable
                ? t('admin.licenseActivity.confirm.resumeBody', {
                    edition: cardLabel(confirmToggle, t)
                  })
                : t('admin.licenseActivity.confirm.pauseBody', {
                    edition: cardLabel(confirmToggle, t)
                  })}
            </p>
            <div className="flex justify-end gap-[10px]">
              <button
                className="px-[16px] py-[8px] rounded-[8px] bg-gray-100 hover:bg-gray-200 text-content-dark text-[13px]"
                onClick={() => setConfirmToggle(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                className={`px-[16px] py-[8px] rounded-[8px] text-white text-[13px] ${
                  confirmToggle.enable
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                onClick={() => {
                  toggleMutation.mutate({
                    product: confirmToggle.product,
                    edition: confirmToggle.edition,
                    enable: confirmToggle.enable
                  });
                  setConfirmToggle(null);
                }}
              >
                {confirmToggle.enable
                  ? t('admin.licenseActivity.confirm.resume')
                  : t('admin.licenseActivity.confirm.pause')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
